import * as React from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '../../lib/utils';
import { formaterResultat } from './TableauRapport';
import { estNombre, messagesControleRecettes, nombre } from './reportHelpers';

/**
 * Petites briques visuelles des rapports: montant signé, barre empilée des
 * charges, légende, barre de répartition, chiffre en ligne, pastille de
 * signalement. Toujours un texte à côté d'une couleur.
 */

/** Montant aligné en chiffres tabulaires; `signe`: + vert / − rouge. */
export const Montant = ({ valeur, signe = false, fort = false, unite, className }) => {
  const n = estNombre(valeur) ? Number(valeur) : 0;
  const couleur = signe
    ? n < 0
      ? 'text-destructive'
      : n > 0
        ? 'text-success'
        : 'text-muted-foreground'
    : n < 0
      ? 'text-destructive'
      : n === 0
        ? 'text-muted-foreground'
        : '';
  return (
    <span className={cn('whitespace-nowrap tabular-nums', fort && 'font-semibold', couleur, className)}>
      {signe ? formaterResultat(n) || '0' : n < 0 ? `−\u00a0${nombre(-n)}` : nombre(n) || '0'}
      {unite && <span className="ml-1 text-[11px] font-normal text-muted-foreground">{unite}</span>}
    </span>
  );
};

/** Barre horizontale empilée. `parts`: [{ key, label, valeur, couleur }]. `total`: base de la barre. */
export const BarreEmpilee = ({ parts, total, hauteur = 'h-2', className, libelle }) => {
  const base = total || parts.reduce((somme, part) => somme + part.valeur, 0);
  if (!base) {
    return <span className={cn('block w-full rounded-full bg-muted', hauteur, className)} aria-hidden="true" />;
  }
  const description =
    libelle || parts.map((part) => `${part.label} ${nombre(part.valeur)}`).join(', ');
  return (
    <span
      className={cn('flex w-full overflow-hidden rounded-full bg-muted', hauteur, className)}
      role="img"
      aria-label={description}
      title={description}
    >
      {parts.map((part) => (
        <span
          key={part.key}
          className="h-full border-r-2 border-card last:border-r-0"
          style={{ width: `${Math.max(0, (part.valeur / base) * 100)}%`, backgroundColor: part.couleur }}
        />
      ))}
    </span>
  );
};

/** Légende d'une barre: pastille de couleur, nom, montant (facultatif). */
export const Legende = ({ parts, montants = true, className }) => (
  <ul className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}>
    {parts.map((part) => (
      <li key={part.key} className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: part.couleur }} aria-hidden="true" />
        <span>{part.label}</span>
        {montants && <span className="tabular-nums text-foreground">{nombre(part.valeur)}</span>}
      </li>
    ))}
  </ul>
);

/** Chiffre libellé compact: « Recettes 2 025 000 ». */
export const Chiffre = ({ label, valeur, signe = false, fort = false, className, alignement = 'right' }) => (
  <div className={cn('min-w-0', alignement === 'right' ? 'text-right' : 'text-left', className)}>
    <p className="text-[11px] leading-4 text-muted-foreground">{label}</p>
    <p className="text-sm leading-5">
      <Montant valeur={valeur} signe={signe} fort={fort} />
    </p>
  </div>
);

/** Pastille d'un signalement venu du rapport (« Fret non renseigné »). */
export const PastilleSignalement = ({ texte, titre, className }) => {
  const attention = texte === 'Fret non renseigné' || texte === 'Prévu';
  return (
    <span
      title={titre}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-medium leading-4',
        attention
          ? 'border-flame/30 bg-flame/10 text-flame-ink'
          : 'border-border bg-muted/50 text-muted-foreground',
        className,
      )}
    >
      {texte === 'Fret non renseigné' && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />}
      {texte}
    </span>
  );
};

/** Contrôle des recettes du rapport: messages déjà rédigés par le serveur. */
export const EncartControleRecettes = ({ rapport }) => {
  const textes = messagesControleRecettes(rapport);
  if (!textes.length) return null;
  return (
    <div
      role="note"
      data-testid="controle-recettes"
      className="flex w-fit max-w-full flex-col gap-1 rounded-lg border border-flame/30 bg-flame/5 px-3 py-2 text-sm text-flame-ink"
    >
      {textes.map((texte) => (
        <span key={texte} className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {texte}
        </span>
      ))}
    </div>
  );
};

/** Signalements d'un rapport entier, en un encart discret. */
export const EncartSignalements = ({ textes }) => {
  if (!textes?.length) return null;
  return (
    <div
      role="note"
      className="flex flex-wrap items-center gap-2 rounded-lg border border-flame/30 bg-flame/5 px-3 py-2 text-sm text-flame-ink"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      {textes.map((texte) => (
        <span key={texte}>{texte}</span>
      ))}
    </div>
  );
};
